/*
 * Newsletter Popup — vanilla rewrite of orion-v3.0's jQuery + js.cookie
 * popup logic (was in the webpack bundle). Modeled on the cookie + initAll
 * pattern in assets/announcement-bar.js. Handles:
 *
 *   Show         After `data-delay` seconds the card gets `.active`
 *                (CSS fades it in), but only if the `newsletter-popup`
 *                cookie is unset. In the theme editor (Shopify.designMode)
 *                it shows immediately, ignoring the cookie, so the
 *                merchant can always see and style it.
 *
 *   Dismiss      Close-button click removes `.active` and writes
 *                `newsletter-popup=dismissed` for `data-dismiss-days`.
 *
 *   Success      On a successful Klaviyo signup the card gets
 *                `.newsletter-success` (CSS swaps the form for the
 *                success message) and writes `newsletter-popup=success`
 *                for `data-success-days`.
 *
 *   Submit       The form is intercepted and POSTed to its own
 *                `data-ajax-submit` endpoint (Klaviyo's CORS-friendly
 *                list-subscribe AJAX). If that call fails, it falls back
 *                to the form's native `action` (target=_blank) so the
 *                signup still goes through.
 *
 * Loaded globally from snippets/scripts.liquid; binds every
 * [data-newsletter-popup] on load and on shopify:section:load.
 */

const SELECTOR = '[data-newsletter-popup]';
const BANNER_SELECTOR = '[data-newsletter-banner]';
const INSTANCE_KEY = '_newsletterPopupInstance';
const COOKIE_NAME = 'newsletter-popup';

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

class NewsletterPopup {
  constructor(root) {
    this.root = root;
    this.closeBtn = root.querySelector('.contact-modal__close');
    this.form = root.querySelector('form.newsletter-klaviyo-form');
    this.messagesEl = root.querySelector('.klaviyo_messages');
    this.errorEl = root.querySelector('.error_message');
    this.overlay = root.closest('.shopify-section')?.querySelector('[data-newsletter-overlay]') || null;
    this.banner = root.closest('.shopify-section')?.querySelector(BANNER_SELECTOR) || null;

    this.delay = (parseFloat(root.dataset.delay) || 5) * 1000;
    this.dismissDays = parseFloat(root.dataset.dismissDays) || 7;
    this.successDays = parseFloat(root.dataset.successDays) || 365;

    this.showTimer = null;

    this.init();
  }

  init() {
    const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
    const cookie = readCookie(COOKIE_NAME);
    const hasBanner = Boolean(this.banner);

    if (inEditor) {
      // Stay hidden in the theme editor so the popup doesn't sit over every
      // preview. Selecting the section in the editor sidebar still opens it
      // for styling; deselecting hides it again.
      this.sectionEl = this.root.closest('.shopify-section');
      this.onSectionSelect = (e) => {
        if (e.target === this.sectionEl) {
          this.hideBanner();
          this.show();
        }
      };
      this.onSectionDeselect = (e) => {
        if (e.target === this.sectionEl) {
          this.root.classList.remove('active');
          this.hideBanner();
        }
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    } else if (!cookie) {
      if (hasBanner) {
        this.showTimer = setTimeout(() => this.showBanner(), this.delay);
      } else {
        this.showTimer = setTimeout(() => this.show(), this.delay);
      }
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.dismiss();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.dismiss();
      });
    }

    if (this.banner) {
      const bannerClose = this.banner.querySelector('[data-newsletter-banner-close]');
      const bannerSubscribe = this.banner.querySelector('[data-newsletter-banner-subscribe]');

      if (bannerClose) {
        bannerClose.addEventListener('click', (e) => {
          e.preventDefault();
          this.dismissBanner();
        });
      }

      if (bannerSubscribe) {
        bannerSubscribe.addEventListener('click', (e) => {
          e.preventDefault();
          this.hideBanner();
          this.show();
        });
      }
    }

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    this.root[INSTANCE_KEY] = this;
  }

  showBanner() {
    if (this.banner) this.banner.classList.add('active');
  }

  hideBanner() {
    if (this.banner) this.banner.classList.remove('active');
  }

  dismissBanner() {
    this.hideBanner();
    writeCookie(COOKIE_NAME, 'dismissed', this.dismissDays);
  }

  show() {
    this.root.classList.add('active');
  }

  dismiss() {
    this.root.classList.remove('active');
    this.hideBanner();
    writeCookie(COOKIE_NAME, 'dismissed', this.dismissDays);
  }

  succeed() {
    writeCookie(COOKIE_NAME, 'success', this.successDays);
    this.root.classList.add('newsletter-success');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || 'An error occurred. Please try again.';
    if (this.messagesEl) this.messagesEl.classList.add('has-message');
  }

  /*
   * Intercept the submit and POST to Klaviyo's list-subscribe AJAX
   * endpoint (the form's data-ajax-submit). On success swap to the
   * success state; on any failure fall back to the native form action
   * so the signup is never silently lost.
   */
  handleSubmit(event) {
    if (!this.form || !this.form.dataset.ajaxSubmit) return;

    event.preventDefault();

    if (this.errorEl) this.errorEl.textContent = '';
    if (this.messagesEl) this.messagesEl.classList.remove('has-message');

    let endpoint = this.form.dataset.ajaxSubmit;
    if (endpoint.startsWith('//')) endpoint = `https:${endpoint}`;

    const params = new URLSearchParams(new FormData(this.form));
    // Klaviyo expects the non-email profile fields listed in $fields.
    params.set('$fields', 'first_name,last_name');

    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Cache-Control': 'no-cache',
      },
      body: params.toString(),
    })
      .then((response) => response.json())
      .then((data) => {
        const ok = data && (data.success === true || (data.success === undefined && !data.errors));
        if (ok) {
          this.succeed();
        } else {
          const errors = data && data.errors;
          this.showError(Array.isArray(errors) ? errors[0] : errors);
        }
      })
      .catch(() => {
        // Network / CORS failure → let the form submit natively.
        this.form.submit();
      });
  }

  destroy() {
    if (this.showTimer) clearTimeout(this.showTimer);
    if (this.onSectionSelect) document.removeEventListener('shopify:section:select', this.onSectionSelect);
    if (this.onSectionDeselect) document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    delete this.root[INSTANCE_KEY];
  }
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new NewsletterPopup(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
